<template>
    <h3 class="mt-5 mb-4">Do it yourself</h3>

    <p>
        For those who do not wish to seek advice and are comfortable they can manage their own sustainable withdrawal plan and wish to
        construct their own investment portfolio from a wide universe of options from various investment managers, individual shares and
        bonds this option is provided by a wide range of providers. Two of these providers who have given us permission to highlight their
        products are shown below although many more choices for a suitable provider exist.
    </p>
    <p class="mb-5">
        To be clear if you decide to use one of these providers Guiide does not receive any referral payment. We have other commercial
        relationships with these and other providers but have no commercial interest in whether you decide to use them as a product provider.
    </p>

    <sub-section title="Drawdown providers" :show-help="false">
        <div class="table-responsive">
            <table class="table table-providers mb-0">
                <thead>
                    <tr>
                        <th>Provider</th>
                        <th class="w-40">Best suited to?</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="providerPresentationData in providersPresentationData" :key="providerPresentationData.provider.key">
                        <tr>
                            <td class="align-middle">
                                <img :src="providerPresentationData.provider.image"
                                    class="img-fluid"
                                    :alt="providerPresentationData.provider.name" />
                            </td>
                            <td class="text-start align-middle">
                                {{ providerPresentationData.provider.description }}
                            </td>
                            <td class="align-middle">
                                <button type="button"
                                        class="btn btn-warning text-nowrap w-100"
                                        @click="visitProvider(providerPresentationData.provider)">
                                    Visit {{ providerPresentationData.provider.name }}
                                </button>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <p class="mt-5">You can seek other providers on your own or get independent financial advice.</p>
        <p>
            You can do this via our Advisers page, or go to <a href="https://www.moneyhelper.org.uk/" target="_blank">moneyhelper.org.uk</a> if you want
            more help on finding an adviser.
        </p>
    </sub-section>

    <button class="btn btn-secondary" @click="emit('back')">Back</button>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import SubSection from '../common/SubSection.vue';
import { PensionProviderResponse } from "@/guiide/ts/response/pensionProviderResponse";

type ProviderPresentationData = {
    provider: PensionProviderResponse,
    formattedCharge: string,
    saving: string,
    savingColor: string
};

const emit = defineEmits<{(e: 'back'): void;}>();

const providersPresentationData = ref(new Array<ProviderPresentationData>());

onMounted(() => {
    const hl: ProviderPresentationData = {
        provider: {
            "key": "HL",
            "name": "Hargreaves Lansdown",
            "description": "Those wanting to access a wide range of investments in their pension including ready-made solutions. Ongoing support 6 days a week by phone, email, or app, to help you manage your retirement income. You can also consider transferring other pensions.",
            "image": "/content/images/providers/hargreaves-logo.png",
            "url": "https://www.hl.co.uk/retirement/retirement-options-through-hl",
            "chargeForGeneralComparison": 44100,
            "chargeNoteGeneralComparison": "For Hargreaves Lansdown the platform charges are 0.45% p.a. for any pot value up to &pound;250,000 then 0.25% p.a. on each &pound;1 above &pound;250,000 to &pound;1,000,000, then 0.1% p.a. on each &pound;1 above &pound;1,000,000 to &pound;2,000,000, then zero on each &pound;1 above &pound;2,000,000. In addition, we have added 0.3% per annum at all pot sizes for the cost of their default investment fund for long term drawdown. Other lower cost funds are also available.",
            "chargeNotePersonalComparison": "For Hargreaves Lansdown; the platform charges are 0.45% p.a. for any pot value up to £250,000 then 0.25% p.a. on each £1 above £250,000 to £1,000,000, then 0.1% p.a. on each £1 above £1,000,000 to £2,000,000, then zero on each £1 above £2,000,000. In addition, we have added 0.24% per annum at all pot sizes for the cost of a Vanguard target dated retirement fund. Many other funds with lower or higher charges are also available on their platform.",
        },
        formattedCharge: "",
        saving: "",
        savingColor: ""
    };

    const ii: ProviderPresentationData = {
        provider: {
            "key": "II",
            "name": "ii",
            "description": "Those that want to keep more of their gains and income - a low flat fee for their award winning SIPP giving access to all types of investments. Only a few minutes to open an account and help to transfer existing pensions.",
            "image": "/content/images/providers/ii-logo.png",
            "url": "https://www.ii.co.uk/ii-partners/guiide?utm_source=guiide&utm_medium=affiliate&utm_campaign=sipp_main_page",
            "chargeForGeneralComparison": 22600,
            "chargeNoteGeneralComparison": "For Interactive Investor, the platform charges are £12.99 per month for funds over £50,000, £5.99 per month below £50,000. In addition there is a charge of £3.99 per month for each disinvestment to make a payment. We have assumed these fixed costs increase at 2% p.a. In addition, we have added 0.24% p.a at all pot sizes for the cost of a Vanguard target dated retirement fund. Many other funds with lower or higher charges are also available.",
            "chargeNotePersonalComparison": "For Interactive Investor, the platform charges are £12.99 per month for funds over £50,000, £5.99 per month below £50,000. In addition there is a charge of £3.99 per month for each disinvestment to make a payment. We have assumed these fixed costs increase at 2% p.a. In addition, we have added 0.24% p.a at all pot sizes for the cost of a Vanguard target dated retirement fund. Many other funds with lower or higher charges are also available."
        },
        formattedCharge: "",
        saving: "",
        savingColor: ""
    };    

    providersPresentationData.value.push(hl);
    providersPresentationData.value.push(ii);
});

function visitProvider(provider: PensionProviderResponse) {
    window.open(provider.url, "_blank");
}
</script>